import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Button, Form, Nav, Modal } from 'react-bootstrap'
import { Quill } from 'react-quill'
import _ from 'lodash'
import 'react-quill/dist/quill.snow.css'

import * as Constants from '../../helpers/constants'
import showToastNotification from '../widgets/toastify'
import { ApiHelper } from '../../helpers/apihelper'
import '../../static/css/announcement_bar.css'
import {
  TABS,
  CHAR_LIMITS,
  FONT_SIZES,
  TOOLBAR_CONTAINER,
  TOOLTIP_MAP,
  initialUserState,
  initialPublicState,
} from './constants'
import AppAnnouncementMessage from './AppAnnouncementMessage'
import WebsiteAnnouncementMessage from './WebsiteAnnouncementMessage'
import ConfirmationModal from '../../helpers/confirmationModal'

const SizeStyle = Quill.import('attributors/style/size')
SizeStyle.whitelist = FONT_SIZES
Quill.register(SizeStyle, true)

// Custom link blot that stores and applies target="_blank" per link.
const LinkBlot = Quill.import('formats/link')
class CustomLink extends LinkBlot {
  static create(value) {
    const url = typeof value === 'string' ? value : value.url
    const node = super.create(url)
    const target = typeof value === 'object' ? value.target : '_self'
    if (target === '_blank') {
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'noopener noreferrer')
    } else {
      node.removeAttribute('target')
      node.removeAttribute('rel')
    }
    return node
  }

  static formats(node) {
    return {
      url: node.getAttribute('href'),
      target: node.getAttribute('target') || '_self',
    }
  }

  format(name, value) {
    if (name !== 'link') return super.format(name, value)
    if (!value) return super.format(name, value)
    const url = typeof value === 'string' ? value : value.url
    this.domNode.setAttribute('href', CustomLink.sanitize(url))
    const target = typeof value === 'object' ? value.target : '_self'
    if (target === '_blank') {
      this.domNode.setAttribute('target', '_blank')
      this.domNode.setAttribute('rel', 'noopener noreferrer')
    } else {
      this.domNode.removeAttribute('target')
      this.domNode.removeAttribute('rel')
    }
  }
}
Quill.register(CustomLink, true)

const getPlainTextLength = (html) => {
  if (!html || html === '<p><br></p>') return 0
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').length
}

// Quill wraps output in <p> block tags; convert to inline <span> so the
// announcement bar flex layout can center the content correctly.
const toInlineHtml = (html) => {
  if (!html) return html
  return html
    .replace(/<p[^>]*>/g, '<span>')
    .replace(/<\/p>/g, '</span>')
    .replace(/<br\s*\/?>/g, '')
    .trim()
}


const AnnouncementBar = ({ client }) => {
  const [activeTab, setActiveTab] = useState(TABS.USER)
  const [userState, setUserState] = useState(initialUserState)
  const [publicState, setPublicState] = useState(initialPublicState)
  const [loading, setLoading] = useState(false)
  const [linkModal, setLinkModal] = useState({ show: false, url: '', newTab: false })
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const userQuillRef = useRef(null)
  const publicQuillRef = useRef(null)
  const userLastValidDelta = useRef(null)
  const publicLastValidDelta = useRef(null)
  const pendingLinkRef = useRef({ quillRef: null, selection: null })
  const userLinkHandlerRef = useRef(null)
  const publicLinkHandlerRef = useRef(null)

  const openLinkModal = useCallback((quillRef) => {
    const quill = quillRef.current?.getEditor()
    if (!quill) return
    const sel = quill.getSelection(true)
    pendingLinkRef.current = { quillRef, selection: sel }
    const format = sel ? quill.getFormat(sel.index, sel.length) : {}
    const existing = format.link
    setLinkModal({
      show: true,
      url: existing ? (typeof existing === 'string' ? existing : existing.url || '') : '',
      newTab: existing ? (typeof existing === 'object' ? existing.target === '_blank' : false) : false,
    })
  }, [])

  // Stable refs so useMemo modules never need to be recreated.
  userLinkHandlerRef.current = () => openLinkModal(userQuillRef)
  publicLinkHandlerRef.current = () => openLinkModal(publicQuillRef)

  const userQuillModules = useMemo(() => ({
    toolbar: {
      container: TOOLBAR_CONTAINER,
      handlers: { link: () => userLinkHandlerRef.current?.() },
    },
    clipboard: { matchVisual: false },
  }), [])

  const publicQuillModules = useMemo(() => ({
    toolbar: {
      container: TOOLBAR_CONTAINER,
      handlers: { link: () => publicLinkHandlerRef.current?.() },
    },
    clipboard: { matchVisual: false },
  }), [])

  const handleLinkInsert = () => {
    const { quillRef, selection } = pendingLinkRef.current
    const quill = quillRef?.current?.getEditor()
    if (quill && selection) quill.setSelection(selection, 'silent')
    if (quill && linkModal.url.trim()) {
      quill.format('link', { url: linkModal.url.trim(), target: linkModal.newTab ? '_blank' : '_self' })
    }
    setLinkModal({ show: false, url: '', newTab: false })
  }

  const handleLinkRemove = () => {
    const { quillRef, selection } = pendingLinkRef.current
    const quill = quillRef?.current?.getEditor()
    if (quill && selection) {
      quill.setSelection(selection, 'silent')
      quill.format('link', false)
    }
    setLinkModal({ show: false, url: '', newTab: false })
  }

  const handleUserMessageChange = (value, _delta, _source, editor) => {
    if (getPlainTextLength(value) > CHAR_LIMITS[TABS.USER]) {
      const quill = userQuillRef.current?.getEditor()
      if (quill && userLastValidDelta.current) {
        quill.setContents(userLastValidDelta.current, 'silent')
      }
      return
    }
    userLastValidDelta.current = editor.getContents()
    setUserState(prev => ({ ...prev, message: value }))
  }

  const handlePublicMessageChange = (value, _delta, _source, editor) => {
    if (getPlainTextLength(value) > CHAR_LIMITS[TABS.PUBLIC]) {
      const quill = publicQuillRef.current?.getEditor()
      if (quill && publicLastValidDelta.current) {
        quill.setContents(publicLastValidDelta.current, 'silent')
      }
      return
    }
    publicLastValidDelta.current = editor.getContents()
    setPublicState(prev => ({ ...prev, message: value }))
  }

  const handleUserFieldChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setUserState(prev => ({ ...prev, [field]: value }))
  }

  const buildUserPayload = () => {
    const payload = {
      name: 'user_announcement_message',
      value: {
        message: toInlineHtml(userState.message),
        dismissable: userState.dismissable,
      },
    }

    const styles = {}
    if (userState.closeBtnColor) {
      styles.close_btn = { color: userState.closeBtnColor }
    }
    if (userState.bgColor) {
      styles.top_container = { backgroundColor: userState.bgColor }
    }
    if (Object.keys(styles).length > 0) {
      payload.value.styles = styles
    }

    return payload
  }

  const buildPublicPayload = () => ({
    name: 'public_announcement_message',
    value: [toInlineHtml(publicState.message)],
  })

  const handleSubmit = () => {
    const message = activeTab === TABS.USER ? userState.message : publicState.message
    if (!message || message === '<p><br></p>') {
      showToastNotification({ message: 'Message cannot be empty', isSuccess: false })
      return
    }
    setShowConfirmModal(true)
  }

  const handleConfirm = () => {
    const isUser = activeTab === TABS.USER
    const body = isUser ? buildUserPayload() : buildPublicPayload()

    setLoading(true)
    ApiHelper(client, 'UPDATE_SYSPROP').API(client, body)
      .then((response) => {
        if (response.status === Constants.CO_API.SUCCESS) {
          showToastNotification({
            message: 'Announcement bar updated successfully',
            isSuccess: true,
          })
        } else {
          showToastNotification({
            message: _.get(response, 'error.message', 'Failed to update announcement bar'),
            isSuccess: false,
          })
        }
      })
      .catch(() => {
        showToastNotification({ message: 'Something went wrong', isSuccess: false })
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    const toolbar = document.querySelector('.announcement-editor-section .ql-toolbar')
    if (!toolbar) return

    toolbar.querySelectorAll('button, .ql-picker').forEach((el) => {
      const match = Object.keys(TOOLTIP_MAP).find((cls) => el.classList.contains(cls))
      if (match) el.setAttribute('data-tooltip', TOOLTIP_MAP[match])
    })
  }, [activeTab])

  const isEmptyMessage = (msg) => !msg || msg === '<p><br></p>'
  const currentMessage = activeTab === TABS.USER ? userState.message : publicState.message
  const showPreview = !isEmptyMessage(currentMessage) ||
    (activeTab === TABS.USER && (userState.bgColor || userState.closeBtnColor))

  return (
    <>
      <h1 className='header'>Announcement Bar Update</h1>
      <div className='announcement-bar-wrapper'>
        <p className='heading-subtext'>
          Update sticky note content and improve user experience across website and app
        </p>
        <Nav
          variant='tabs'
          activeKey={activeTab}
          onSelect={setActiveTab}
          className='mb-4'
        >
          <Nav.Item>
            <Nav.Link eventKey={TABS.USER}>App Announcement Message</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey={TABS.PUBLIC}>Website Announcement Message</Nav.Link>
          </Nav.Item>
        </Nav>
        {activeTab === TABS.USER && (
          <AppAnnouncementMessage
            userState={userState}
            userQuillRef={userQuillRef}
            userQuillModules={userQuillModules}
            handleUserMessageChange={handleUserMessageChange}
            handleUserFieldChange={handleUserFieldChange}
            setUserState={setUserState}
            getPlainTextLength={getPlainTextLength}
          />
        )}
        {activeTab === TABS.PUBLIC && (
          <WebsiteAnnouncementMessage
            publicState={publicState}
            publicQuillRef={publicQuillRef}
            publicQuillModules={publicQuillModules}
            handlePublicMessageChange={handlePublicMessageChange}
            getPlainTextLength={getPlainTextLength}
          />
        )}
        {showPreview && (
          <div className='announcement-preview-section mt-4'>
            <p
              className='fw-semibold mb-2'
              style={{ fontSize: '14px', color: '#555' }}
            >
              Preview
            </p>
            <div className='announcement-preview-mock-header'>
              <div className='announcement-preview-mock-logo' />
              <div
                className='announcement-preview-bar'
                style={{
                  background: activeTab === TABS.USER && userState.bgColor
                    ? userState.bgColor
                    : undefined,
                }}
              >
                {!isEmptyMessage(currentMessage) && (
                  <div
                    className='announcement-preview-message'
                    dangerouslySetInnerHTML={{ __html: toInlineHtml(currentMessage) }}
                  />
                )}
                {activeTab === TABS.USER && userState.dismissable && (
                  <span
                    className='announcement-preview-close'
                    style={userState.closeBtnColor ? { color: userState.closeBtnColor } : {}}
                  >
                    ✕
                  </span>
                )}
              </div>
              <div className='announcement-preview-mock-nav' />
            </div>
            <p className='announcement-preview-hint'>Appears centered in the top navigation bar</p>
          </div>
        )}
        <div className='announcement-bar-submit mt-4'>
          <Button
            variant='outline-success'
            onClick={handleSubmit}
            disabled={loading || isEmptyMessage(currentMessage)}
          >
            {loading ? 'Publishing...' : 'Publish Announcement Bar'}
          </Button>
        </div>
      </div>
      <Modal
        show={linkModal.show}
        onHide={() => setLinkModal(m => ({ ...m, show: false }))}
        centered
        size='sm'
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '16px' }}>Insert Link</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className='mb-3'>
            <Form.Label className='mb-1'>URL</Form.Label>
            <Form.Control
              type='url'
              placeholder='https://example.com'
              value={linkModal.url}
              onChange={(e) => setLinkModal(m => ({ ...m, url: e.target.value }))}
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && linkModal.url.trim()) handleLinkInsert() }}
            />
          </Form.Group>
          <Form.Check
            type='switch'
            id='link-modal-new-tab'
            label='Open in new tab'
            checked={linkModal.newTab}
            onChange={(e) => setLinkModal(m => ({ ...m, newTab: e.target.checked }))}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='outline-danger'
            size='sm'
            onClick={handleLinkRemove}
          >
            Remove
          </Button>
          <Button
            variant='outline-secondary'
            size='sm'
            onClick={() => setLinkModal(m => ({ ...m, show: false }))}
          >
            Cancel
          </Button>
          <Button
            variant='outline-success'
            size='sm'
            onClick={handleLinkInsert}
            disabled={!linkModal.url.trim()}
          >
            Insert
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmationModal
        show={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title='Publish Announcement Bar'
        message='Are you sure you want to publish this announcement message?'
        onConfirm={handleConfirm}
        confirmText='Yes, Publish'
        cancelText='No'
      />
    </>
  )
}

export default AnnouncementBar
